import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Url } from '@df/core/service/url';
import { Status } from '@df/core/status';
import { PwaIntegrationService } from '@df/module-pwa/pwa-integration.service';
import { Customer } from '@df/session/api/customer';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { LocalCustomerModel } from '../../customer/customer.model.decorator';
import { NAntavo } from '../../module/antavo/antavo.interface';
import { AntavoService } from '../../module/antavo/antavo.service';
import { LocalBasket } from '../local-basket';

@Component({
    selector: 'antavo-preview-points-banner',
    templateUrl: './antavo-preview-points-banner.html',
    styleUrls: ['./antavo-preview-points-banner.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [TranslationModule, NgClass, RouterLink]
})
export class AntavoPreviewPointsBannerComponent extends UiComponent implements OnInit, OnDestroy {
    private readonly antavoService = inject(AntavoService);
    private readonly basket = inject(LocalBasket);
    private readonly customer = inject(Customer);
    private readonly urlService = inject(Url);
    private pwaIntegrationService = inject(PwaIntegrationService);

    protected pointsPreview?: NAntavo.IPointsPreviewData;
    protected status = new Status();

    get customerModel(): LocalCustomerModel {
        return this.customer.model as LocalCustomerModel;
    }

    get points(): number {
        return this.pointsPreview?.purchase?.points || 0;
    }

    get signInBanner(): boolean {
        return !this.customerModel.isLoggedIn && this.basket.qty > 0 && !!this.pwaIntegrationService.isStandalone;
    }

    override ngOnInit(): void {
        // Subscribe to basket changes to update points preview
        this.subscriptions.add(
            this.basket.subscribe(() => {
                this.loadPointsPreview();
            })
        );

        this.subscriptions.add(
            this.customerModel.obs.status.subscribe(() => {
                if (this.customerModel.isLoggedIn) {
                    this.loadPointsPreview();
                } else {
                    this.pointsPreview = undefined;
                    this.detectChanges();
                }
            })
        );

        this.subscriptions.add(
            this.status.subscribe(() => {
                this.detectChanges();
            })
        );

        // Initial load
        this.loadPointsPreview();
        super.ngOnInit();
    }

    private async loadPointsPreview(): Promise<void> {
        if (!this.basket.qty || !this.customerModel?.loyaltyId) {
            this.pointsPreview = undefined;
            this.detectChanges();
            return;
        }

        try {
            const transactionItems: NAntavo.ITransactionItem[] = this.basket.items.map(item => ({
                product_id: item.product?.data?.id?.toString(),
                product_name: item.product?.data?.name,
                product_url: this.urlService.toUrl(item.product?.data?.url || ''),
                price: item.data.price,
                quantity: item.qty,
                subtotal: item.data.rowPrice
            }));

            this.pointsPreview = await this.antavoService.getPointsPreview(
                {
                    loyaltyId: this.customerModel.loyaltyId,
                    transaction_id: this.basket.data.quote_id?.toString() || Date.now().toString(),
                    total: this.basket.totals?.subtotal || 0,
                    items: transactionItems
                },
                this.status
            );
            this.detectChanges();
        } catch (error) {
            console.error('Failed to load points preview:', error);
            this.pointsPreview = undefined;
        }
    }
}
